import type { PackageListModel } from '../misc/PackageListModel';
import { action, computed, observable, reaction } from 'mobx';
import _ from 'lodash';
import { message } from 'antd';
import { TestProject } from '../types';
import type { TestProjectInputsChanged } from '../types';
import { calculateBaselineImpact } from '../misc/TestProjectBaselineImpact';
import { TestProjectService } from '../services';
import { RulesService } from '../../rules/services';
import TestProjectInputSelectionStore from './TestProjectInputSelectionStore';
import type TestProjectInputEditorDraft from '../models/TestProjectInputEditorDraft';
import { TestProjectInputEditDraft, TestProjectInputMappingDraft } from '../models/TestProjectInputEditorDraft';
import type { TestProjectPreparedInputRequest } from '../models/TestProjectInputEditorDraft';
import type { TestProjectInputEditorSource } from '../models/TestProjectInputEditorDraft';
import type TestProjectDataStore from './TestProjectDataStore';

// Owns existing-project input editing: opening a draft, preparing impact and submitting changes.
// Selection rules live in the selection store; per-opening values live in the draft.
export default class TestProjectInputEditorStore {
    @observable.ref
    draft: TestProjectInputEditorDraft | undefined;

    // This guard belongs to the store because closing a draft cannot cancel a write already sent to the server.
    @observable
    isSubmitting: boolean = false;

    constructor(
        private service: TestProjectService,
        private rulesService: RulesService,
        private loadDocuments: (projectId: string, packageIds: string[]) => Promise<PackageListModel[]>,
        private onInputsChanged: (
            change: TestProjectInputsChanged,
            source: TestProjectInputEditorSource
        ) => Promise<void>
    ) {
        reaction(
            () => !this.draft || this.draft.editingSource.isCurrent(),
            isCurrent => {
                if (!isCurrent) {
                    this.close();
                }
            }
        );
        reaction(
            () => ({
                draft: this.draft,
                inputType: this.draft?.inputSelectionStore.topicInputsType,
                error: this.draft?.inputSelectionStore.metadataError
            }),
            ({ draft, error }) => {
                if (draft?.operation === 'mapping' && error) {
                    message.error(error);
                }
            }
        );
    }

    @computed
    get mappingDraft() {
        return this.draft?.operation === 'mapping' ? this.draft : undefined;
    }

    @computed
    get isSourceEditable() {
        return !!this.draft && this.canEditSource(this.draft.editingSource);
    }

    @computed
    get canEdit() {
        const draft = this.draft;

        if (!draft || !this.isSourceEditable) {
            return false;
        }

        return draft.phase.kind === 'editing';
    }

    // Dashboard and import share this editor, but each presentation may render only its own source draft.
    getDraftFor(dataStore: TestProjectDataStore | undefined) {
        const draft = this.draft;

        return draft?.editingSource.dataStore === dataStore ? draft : undefined;
    }

    @computed
    get canSave() {
        const draft = this.draft;

        if (!draft || !this.canEdit) {
            return false;
        }

        const inputSelectionStore = draft.inputSelectionStore;

        if (
            !inputSelectionStore.topicInputsType ||
            inputSelectionStore.isLoadingMetadata ||
            inputSelectionStore.metadataError
        ) {
            return false;
        }

        if (draft.operation === 'direct') {
            return !inputSelectionStore.hasInvalidTopics;
        }

        if (
            !draft.baselineImpact ||
            draft.isLoadingBaselines ||
            draft.baselinesError ||
            !draft.availableTargetInputTypes.includes(inputSelectionStore.topicInputsType)
        ) {
            return false;
        }

        const targetIds = _.compact(draft.mappings.map(mapping => mapping.newValue));
        const availableIds = inputSelectionStore.availableInputsById;

        return targetIds.length > 0 && targetIds.every(id => availableIds.has(id));
    }

    @action.bound
    openDirect(source: TestProjectInputEditorSource) {
        return this.open('direct', undefined, source);
    }

    @action.bound
    openMapping(source: TestProjectInputEditorSource, mappingTargetInputType?: 'Bindings') {
        return this.open('mapping', mappingTargetInputType, source);
    }

    @action.bound
    close() {
        this.draft = undefined;
    }

    @action.bound
    cancelEditing() {
        const draft = this.draft;

        if (!draft || this.isSubmitting) {
            return;
        }

        if (draft.pendingTopicRemoval) {
            this.cancelTopicRemoval();
            return;
        }

        if (draft.isConfirming) {
            this.backToEditing();
            return;
        }

        this.close();
    }

    @action.bound
    requestTopicRemoval(topicId: string) {
        const draft = this.draft;

        if (!draft || draft.operation !== 'direct' || !this.canEdit) {
            return;
        }

        const selection = draft.inputSelectionStore;
        // Draft-only selections have no saved baselines to remove.
        if (!selection.isSavedInput(topicId)) {
            selection.removeTopic(topicId);
            return;
        }

        draft.phase = { kind: 'removing-topic', topicId };
    }

    @action.bound
    cancelTopicRemoval() {
        const draft = this.draft;

        if (!draft || !draft.pendingTopicRemoval) {
            return;
        }

        draft.phase = { kind: 'editing' };
    }

    @action.bound
    confirmTopicRemoval() {
        const draft = this.draft;
        const topicId = draft?.pendingTopicRemoval;

        if (!draft || !topicId || !this.isSourceEditable) {
            return;
        }

        draft.phase = { kind: 'editing' };

        draft.inputSelectionStore.removeTopic(topicId);
    }

    @action.bound
    changeMappingInputType(inputType: TestProject['inputsType']) {
        const draft = this.editableMappingDraft;

        if (!draft || !draft.availableTargetInputTypes.includes(inputType)) {
            return;
        }

        draft.inputSelectionStore.setTopicInputsType(inputType);
        draft.inputSelectionStore.selectedAppForBindings = '';
        draft.inputSelectionStore.selectedInputGroupForBindings = '';
        this.clearMappings(draft);
    }

    @action.bound
    changeMappingApplication(applicationId: string | undefined) {
        const draft = this.editableMappingDraft;

        if (!draft) {
            return;
        }

        draft.inputSelectionStore.selectedAppForBindings = applicationId || '';
        draft.inputSelectionStore.selectedInputGroupForBindings = '';
        this.clearMappings(draft);
    }

    @action.bound
    changeMappingInputGroup(groupId: string | undefined) {
        const draft = this.editableMappingDraft;

        if (!draft) {
            return;
        }

        draft.inputSelectionStore.selectedInputGroupForBindings = groupId || '';
        this.clearMappings(draft);
    }

    @action.bound
    changeMappingTarget(topicId: string, targetId: string | undefined) {
        const draft = this.editableMappingDraft;

        if (!draft) {
            return;
        }

        const mapping = draft.mappings.find(row => row.sourceTopic.topicId === topicId);

        if (mapping) {
            mapping.newValue = targetId;
        }
    }

    @action.bound
    async retryMetadata() {
        const draft = this.draft;

        if (!draft || !this.canEdit) {
            return;
        }

        await draft.inputSelectionStore.loadMetadata();
    }

    @action.bound
    async prepareSave() {
        // Reopening even the same project creates a new draft; only this exact draft may receive the read.
        const draft = this.draft;

        if (!draft || !this.canSave) {
            return;
        }

        const inputSelectionStore = draft.inputSelectionStore;
        const inputsType = inputSelectionStore.topicInputsType;

        if (!inputsType) {
            return;
        }

        const replacesTagsWithBindings = draft.originalProject.inputsType === 'Tags' && inputsType === 'Bindings';
        const request: TestProjectPreparedInputRequest = {
            inputsType,
            applicationId:
                inputSelectionStore.topicInputsType === 'Bindings'
                    ? inputSelectionStore.selectedAppForBindings
                    : undefined,
            inputGroupId:
                inputSelectionStore.topicInputsType === 'Bindings'
                    ? inputSelectionStore.selectedInputGroupForBindings || undefined
                    : undefined,
            topics: [...inputSelectionStore.topics],
            mappings: [],
            impact: undefined
        };

        if (draft.operation === 'mapping') {
            request.mappings = _.flatMap(draft.mappings, mapping =>
                mapping.newValue ? [{ topicId: mapping.sourceTopic.topicId, newValue: mapping.newValue }] : []
            );
        }

        let requiresConfirmation = false;
        draft.error = undefined;
        // Freeze draft commands while impact is read, so confirmation describes exactly the captured request.
        draft.phase = { kind: 'preparing' };

        try {
            if (draft.operation === 'mapping' || draft.changesInputType) {
                const { baselines, documents } = await this.loadImpactData(draft);

                if (draft !== this.draft) {
                    return;
                }

                const retainedTopicIds =
                    draft.operation === 'mapping' ? request.mappings.map(mapping => mapping.topicId) : [];

                request.impact = calculateBaselineImpact(
                    draft.originalProject.topics,
                    baselines,
                    retainedTopicIds,
                    documents
                );
                requiresConfirmation =
                    draft.operation === 'mapping' ? request.impact.removed.length > 0 : replacesTagsWithBindings;
            }

            if (draft !== this.draft) {
                return;
            }

            if (requiresConfirmation) {
                draft.phase = { kind: 'confirmation', request };
            } else {
                draft.phase = { kind: 'editing' };
                await this.submit(draft, request);
            }
        } catch (error) {
            if (draft === this.draft) {
                draft.phase = { kind: 'editing' };
                draft.error = 'Unable to load baseline impact. Please try Save again.';

                if (draft.operation === 'mapping') {
                    message.error(draft.error);
                }
            }
        }
    }

    @action.bound
    backToEditing() {
        const draft = this.draft;

        if (!draft || this.isSubmitting || draft.isPreparing) {
            return;
        }

        draft.phase = { kind: 'editing' };
        draft.error = undefined;
    }

    @action.bound
    async confirmSave() {
        const draft = this.draft;

        if (!draft || draft.phase.kind !== 'confirmation') {
            return;
        }

        await this.submit(draft, draft.phase.request);
    }

    private async submit(draft: TestProjectInputEditorDraft, request: TestProjectPreparedInputRequest) {
        if (draft !== this.draft || !this.isSourceEditable) {
            return;
        }

        this.isSubmitting = true;
        draft.error = undefined;
        let successful = false;

        try {
            const result =
                draft.operation === 'direct'
                    ? await this.service.updateTestProjectTopics(
                          draft.originalProject.projectId,
                          draft.originalProject.id,
                          request.topics,
                          request.inputsType,
                          request.applicationId,
                          request.inputGroupId
                      )
                    : await this.service.updateTestProjectTopicsType(
                          draft.originalProject.projectId,
                          draft.originalProject.id,
                          request.mappings,
                          request.inputsType,
                          request.applicationId,
                          request.inputGroupId
                      );

            if (result.isErr()) {
                if (draft === this.draft) {
                    message.error(
                        result.error.data?.title || result.error.text || 'Unable to save Test Project inputs.'
                    );
                }

                return;
            }

            successful = true;
            message.success(
                draft.operation === 'direct' ? 'Test project topics updated successfully' : 'Successfully mapped topics'
            );
        } catch (error) {
            if (draft === this.draft) {
                message.error(
                    draft.operation === 'direct' ? 'Failed to update test project topics' : 'Failed to submit mapping'
                );
            }
        } finally {
            this.isSubmitting = false;
        }

        if (!successful) {
            return;
        }

        // The write is acknowledged. Close its draft before refreshes so a read failure cannot offer a second save.
        if (draft === this.draft) {
            this.close();
        }

        try {
            // Mapping preserves topic identity. A direct type change replaces inputs; other direct changes are edits.
            const replacesInputType = draft.originalProject.inputsType !== request.inputsType;
            let outcome: TestProjectInputsChanged['outcome'] = 'editing';

            if (draft.operation === 'mapping') {
                outcome = 'mapping';
            } else if (replacesInputType) {
                outcome = 'replacement';
            }

            const change: TestProjectInputsChanged = {
                outcome,
                projectId: draft.originalProject.projectId,
                testProjectId: draft.originalProject.id,
                impact: request.impact
            };
            await this.onInputsChanged(change, draft.editingSource);
        } catch (error) {
            console.error('Failed to refresh after updating Test Project inputs', error);
        }
    }

    // Identity and editability are separate: a source can remain current while a refresh blocks edits.
    // Check these again before submission; disabled controls alone cannot protect a delayed confirmation.
    private canEditSource(source: TestProjectInputEditorSource) {
        return (
            !this.isSubmitting && source.isCurrent() && source.dataStore.canUseProject && (source.canEdit?.() ?? true)
        );
    }

    private get editableMappingDraft() {
        const draft = this.draft;

        return draft?.operation === 'mapping' && this.canEdit ? draft : undefined;
    }

    @action.bound
    private open(
        operation: 'direct' | 'mapping',
        mappingTargetInputType: 'Bindings' | undefined,
        editingSource: TestProjectInputEditorSource
    ) {
        const dataStore = editingSource.dataStore;

        if (!dataStore.testProject || !this.canEditSource(editingSource)) {
            return false;
        }

        const source = dataStore.testProject;

        // Live project refreshes must not alter the source topics used to prepare this editor's request.
        const capturedSource = {
            ...source,
            topics: source.topics.map(topic => ({ ...topic })),
            packageIds: [...source.packageIds]
        };

        const projectId = dataStore.projectId;
        const inputSelectionStore: TestProjectInputSelectionStore = new TestProjectInputSelectionStore(
            projectId,
            this.service,
            this.rulesService,
            operation === 'direct' ? source.inputsType : undefined,
            () => this.draft?.inputSelectionStore === inputSelectionStore && this.canEdit
        );

        if (operation === 'direct') {
            inputSelectionStore.initialize(capturedSource);
        } else if (mappingTargetInputType !== source.inputsType) {
            inputSelectionStore.topicInputsType = mappingTargetInputType;
        }

        if (operation === 'direct') {
            this.draft = new TestProjectInputEditDraft(capturedSource, inputSelectionStore, editingSource);
        } else {
            this.draft = new TestProjectInputMappingDraft(
                capturedSource,
                inputSelectionStore,
                editingSource,
                mappingTargetInputType
            );
            this.loadMappingBaselines(this.draft);
        }

        this.draft.inputSelectionStore.loadMetadata();

        return true;
    }

    @action.bound
    private async loadMappingBaselines(draft: TestProjectInputMappingDraft) {
        if (draft.isLoadingBaselines) {
            return;
        }

        draft.isLoadingBaselines = true;
        draft.baselinesError = undefined;
        draft.baselines = undefined;

        try {
            const { baselines, documents } = await this.loadImpactData(draft);

            if (draft !== this.draft) {
                return;
            }

            draft.baselines = baselines;
            draft.documents = documents;
        } catch (error) {
            if (draft === this.draft) {
                draft.baselinesError = 'Failed to load baseline impact. Try again before saving.';
                message.error(draft.baselinesError);
            }
        } finally {
            if (draft === this.draft) {
                draft.isLoadingBaselines = false;
            }
        }
    }

    private async loadImpactData(draft: TestProjectInputEditorDraft) {
        const result = await this.service.getTestProjectBaselines(
            draft.originalProject.projectId,
            draft.originalProject.id
        );

        if (result.isErr()) {
            throw result.error;
        }

        const packageIds = [...new Set(result.value.map(baseline => baseline.packageId))];
        // Names are display data; baseline IDs still identify affected documents if their names cannot be read.
        const documents = await this.loadDocuments(draft.originalProject.projectId, packageIds).catch(() => []);

        return { baselines: result.value, documents };
    }

    @action.bound
    private clearMappings(draft: TestProjectInputMappingDraft) {
        draft.mappings.forEach(mapping => {
            mapping.newValue = undefined;
        });
    }
}
